/* eslint-disable max-len */
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

import {
  ChargerActivityResponseStoreModel,
  ChargingTimelineResponseStoreModel,
  EvseActivityGraphDataStoreModel,
  EvseActivityGraphDataUnitStoreModel,
  EvseActivityResponseStoreModel,
  EvseReferenceStoreModel,
} from '@domain/charger-details/charger-details.models';
import { ConnectorTypeStoreModelEnum, EvseStatusStoreModelEnum, HealthGradeStoreModelEnum } from '@domain/single-site/single-site.enums';

import {
  isChargingTimelineEventVisible,
  mapChargingTimelineEventStoreModelToTimelineEvent,
} from '@shared/components/asset-details-dialog/helpers/charging-timeline-event-mapping';
import { KwPipe } from '@shared/pipes/kw';
import { isDefined } from '@shared/utils/is-defined';

import { AiInsightsSectionViewModel } from '../components/ai-insights-section/ai-insights-section.view-model';
import { ChargerDetailsGraphMtuData, ChargerDetailsGraphViewModel } from '../components/charger-details-graph';
import { ChargerDetailsGraphSectionViewModel } from '../components/charger-details-graph-section';
import { ChargerStatsViewModel } from '../components/charger-stats';
import { ConnectorDetailsCardViewModel } from '../components/connector-details-card';
import { ConnectorEventTimelineViewModel } from '../components/connector-event-timeline';
import { ConnectorEvseOverviewChartSectionViewModel } from '../components/connector-evse-overview-chart-section/connector-evse-overview-chart-section.view-model';
import { ConnectorEvseOverviewChartMtuData } from '../components/connector-evse-overview-chart/connector-evse-overview-chart.view-model';
import { EventTimelineViewModel } from '../components/event-timeline/event-timeline.view-model';
import { EvseStatsViewModel } from '../components/evse-stats/evse-stats.view-model';
import { IssuesSectionViewModel } from '../components/issues-section/issues-section.view-model';
import {
  ConnectorDetailsCardLoadingState,
  UntranslatedConnectorDetailsCardLoadingState,
  UntranslatedConnectorDetailsCardViewModel,
  UntranslatedConnectorEventTimelineViewModel,
  UntranslatedEventTimelineViewModel,
} from '../single-site-charger-details.view-model';

export {
  mapChargingTimelineEventStoreModelToTimelineEvent,
  translateChargingTimelineEventStoreModel,
} from '@shared/components/asset-details-dialog/helpers/charging-timeline-event-mapping';

const activeStatuses = new Set([EvseStatusStoreModelEnum.Charging, EvseStatusStoreModelEnum.Queued]);

export const mapChargerActivityToChargerDetailsGraphSectionViewModel = (
  activity: ChargerActivityResponseStoreModel
): ChargerDetailsGraphSectionViewModel => {
  return {
    chargerName: activity.chargerName,
    graphVm: mapChargerActivityToChargerDetailsGraphViewModel(activity),
    connectorNames: activity.evses.map((evse) => evse.name),
    hasCharging: (activity.powerGraphData.data ?? []).some((unit) => unit.charging),
    hasFaulted: false, // TODO this functionality might not be needed
  };
};

export const mapChargerActivityToChargerDetailsGraphViewModel = (
  activity: ChargerActivityResponseStoreModel
): ChargerDetailsGraphViewModel => {
  return {
    connectorsCount: activity.evses.length,
    data: (activity.powerGraphData.data ?? []).map((unitData): ChargerDetailsGraphMtuData => {
      const sortedPowerUnits = activity.evses.map((evse) => {
        return unitData.powerUnits?.find((pu) => pu.id === evse.id);
      });

      return {
        mtu: DateTime.fromISO(unitData.timestamp),
        connectorPowerKwValues: sortedPowerUnits.map((powerUnit) => powerUnit?.powerKw ?? null),
        charging: unitData.charging,
        error: false, // TODO this functionality might not be needed
      };
    }),
  };
};

export const mapChargerActivityToChargerStatsViewModel = (_activity: ChargerActivityResponseStoreModel): ChargerStatsViewModel => {
  // TODO mock
  return {
    lastPingMsec: 600,
    lastPingGrade: HealthGradeStoreModelEnum.Warning,
    latencyMsec: 7500,
    latencyGrade: HealthGradeStoreModelEnum.Good,
    readWriteError: null,
    readWriteErrorGrade: HealthGradeStoreModelEnum.Good,
    lastMeterReadingMsec: 17_600,
    lastMeterReadingGrade: HealthGradeStoreModelEnum.Error,
    lastHeartbeatMsec: 17_700,
    lastHeartbeatGrade: HealthGradeStoreModelEnum.Warning,
  };
};

export const mapChargerActivityToIssuesSectionViewModel = (activity: ChargerActivityResponseStoreModel): IssuesSectionViewModel => {
  return {
    chargerName: activity.chargerName,
    issues: [], // TODO mock
  };
};

export const mapChargerActivityToAiInsightsSectionViewModel = (activity: ChargerActivityResponseStoreModel): AiInsightsSectionViewModel => {
  return {
    chargerName: activity.chargerName,
    insights: [
      {
        title: 'Charger maintenance',
        description: `The charger has provided 1.5 MWh of energy to 14 vehicles over the past 1.4 years. It is recommended that an electric engineer checks vital components to ensure the longevity of the charger.`,
      },
      {
        title: 'Charger 14 & 1514 incompatibility',
        description: `We have noticed that 1514 has a 86% contactor fault rate with Charger 14, significantly higher than with any other charger. Consider always parking 1514 in front of an other charger.`,
      },
    ], // TODO mock
  };
};

export const mapEvseAndDetailsActivityToUntranslatedConnectorDetailsCardLoadingState = (
  evseId: string,
  evses: EvseReferenceStoreModel[],
  activityIsLoading: boolean,
  activity: EvseActivityResponseStoreModel | undefined,
  activityTimelineIsLoading: boolean,
  activityTimeline: ChargingTimelineResponseStoreModel | undefined,
  hasManageBasicCommandsPermissions: boolean,
  hasManagePowerCommandPermissions: boolean,
  siteId: string
): UntranslatedConnectorDetailsCardLoadingState => {
  const evse = evses.find((evse) => evse.id === evseId)!;

  return {
    id: evse.id,
    name: evse.name,
    isLoading: !!activityIsLoading,
    data:
      activity &&
      mapEvseActivityToConnectorDetailsCardViewModel(
        evse.id,
        evse.name,
        evse.connectorType,
        activity,
        activityTimelineIsLoading,
        activityTimeline,
        hasManageBasicCommandsPermissions,
        hasManagePowerCommandPermissions,
        siteId
      ),
  };
};

export const mapEvseActivityToConnectorDetailsCardViewModel = (
  evseId: string,
  evseName: string,
  connectorType: ConnectorTypeStoreModelEnum,
  activity: EvseActivityResponseStoreModel,
  activityTimelineIsLoading: boolean,
  activityTimeline: ChargingTimelineResponseStoreModel | undefined,
  hasManageBasicCommandsPermissions: boolean,
  hasManagePowerCommandPermissions: boolean,
  siteId: string
): UntranslatedConnectorDetailsCardViewModel => {
  const lastEventTimestampISO =
    activityTimeline && activityTimeline.events.length > 0 && activityTimeline.events[activityTimeline.events.length - 1].timestamp;
  const lastEventTimestamp = lastEventTimestampISO ? DateTime.fromISO(lastEventTimestampISO) : undefined;

  const oldestTimestampISO = activityTimeline?.oldest;
  const oldestTimestamp = oldestTimestampISO ? DateTime.fromISO(oldestTimestampISO) : undefined;

  const hasMoreEvents = !!lastEventTimestamp && !!oldestTimestamp && lastEventTimestamp > oldestTimestamp;

  return {
    connectorId: evseId,
    connectorName: evseName,
    siteId,
    status: activity.evseStatus,
    connectorType,
    issues:
      evseId === 'evse3'
        ? [
            // TODO mock
            {
              title: 'Charger not ramping!',
              description:
                'The bus is not allowing more power. Even though we would allow the charger to ramp up to 35kW, the bus does is not taking more than 28kW. Please get it checked out by an electrician ASAP.',
            },
          ]
        : [],
    aiInsights:
      evseId === 'evse2'
        ? [
            {
              title: 'AI Insight',
              description: 'This is a smart AI Insight to the operators about the bus that is being charged at the moment.',
            },
          ]
        : [], // TODO mock
    evseStatsVm: mapEvseActivityToEvseStatsViewModel(activity, siteId),
    connectorEvseOverviewChartSectionVm: mapEvseActivityGraphDataStoreModelToConnectorEvseOverviewChartSectionViewModel(activity.graphData),
    connectorEventTimelineVm: {
      connectorName: evseName,
      pastEvents: !activeStatuses.has(activity.evseStatus),
      events: activityTimeline?.events ?? [],
      expanded: false,
      infiniteScrollEnabled: true,
      loading: activityTimelineIsLoading,
      hasMoreEvents,
    },
    actionsShown: hasManageBasicCommandsPermissions,
    powerLimitActionShown: hasManagePowerCommandPermissions,
    powerOverride: activity.powerOverride,
    hasActiveSession: activity.hasActiveSession,
    vehicleId: activeStatuses.has(activity.evseStatus) ? activity.vehicleUUID : undefined,
    vehicleDisplayName: activeStatuses.has(activity.evseStatus) ? activity.vehicleName : undefined,
  };
};

export const mapEvseActivityToEvseStatsViewModel = (activity: EvseActivityResponseStoreModel, siteId: string): EvseStatsViewModel => {
  return {
    siteId,
    vehicleId: activeStatuses.has(activity.evseStatus) ? activity.vehicleUUID : undefined,
    vehicleDisplayName: activeStatuses.has(activity.evseStatus) ? activity.vehicleName : undefined,
    departureTime: activity.departureTime ? DateTime.fromISO(activity.departureTime) : undefined,
    powerKw: activity.activePowerKw ?? 0,
    connectorMaxPowerKw: activity.maxPowerKw,
    energyDeliveredKwh: activity.deliveredEnergyKwh,
    forecastedDepartureSocPercentage: activity.forecastedDepartureSoc && activity.forecastedDepartureSoc * 100,
    socPercentage: activeStatuses.has(activity.evseStatus) ? activity.soc && activity.soc * 100 : undefined,
    // TODO remove this available check after BE fixes it
    chargingLimitKw: activity.evseStatus === EvseStatusStoreModelEnum.Available ? undefined : (activity.evsePowerLimitKw ?? 0),
    energyRemainingKwh: activity.remainingEnergyKwh,
    lastSeenVehicleDisplayName: activeStatuses.has(activity.evseStatus) ? undefined : activity.vehicleName,
    lastSocPercentage: activeStatuses.has(activity.evseStatus) ? undefined : activity.soc && activity.soc * 100,
    powerOverride: activity.powerOverride,
  };
};

export const mapEvseActivityGraphDataStoreModelToConnectorEvseOverviewChartSectionViewModel = (
  graphData: EvseActivityGraphDataStoreModel
): ConnectorEvseOverviewChartSectionViewModel | undefined => {
  if (!graphData.data) {
    return;
  }

  const hasAtLeastOneData = graphData.data?.some((unit) => {
    return isDefined(unit.powerKw) || isDefined(unit.powerLimitKw) || isDefined(unit.soc) || unit.faulted;
  });

  if (!hasAtLeastOneData) {
    return;
  }

  return {
    hasFault: !!graphData.data?.some((unit) => unit.faulted),
    chartVm: {
      data: graphData.data?.map((unit) => mapEvseActivityGraphDataUnitStoreModelToConnectorEvseOverviewChartMtuData(unit)),
    },
  };
};

export const mapEvseActivityGraphDataUnitStoreModelToConnectorEvseOverviewChartMtuData = (
  unit: EvseActivityGraphDataUnitStoreModel
): ConnectorEvseOverviewChartMtuData => {
  return {
    mtu: DateTime.fromISO(unit.timestamp),
    evSocPercentage: unit.soc && unit.soc * 100,
    powerKw: unit.powerKw,
    powerLimitKw: unit.powerLimitKw,
    faulted: unit.faulted,
  };
};

export const mapUntranslatedEventTimelineVmToEventTimelineVm = (
  untranslatedEventTimelineVm: UntranslatedEventTimelineViewModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): EventTimelineViewModel => {
  return {
    chargerName: untranslatedEventTimelineVm.chargerName,
    loading: untranslatedEventTimelineVm.loading,
    events: untranslatedEventTimelineVm.events
      .filter((event) => isChargingTimelineEventVisible(event))
      .map((event) =>
        mapChargingTimelineEventStoreModelToTimelineEvent(untranslatedEventTimelineVm.evses, event, translateService, kwPipe, siteId)
      ),
    infiniteScrollEnabled: untranslatedEventTimelineVm.infiniteScrollEnabled,
    hasMoreEvents: untranslatedEventTimelineVm.hasMoreEvents,
  };
};

export const translateConnectorDetailsCardLoadingState = (
  untranslatedVm: UntranslatedConnectorDetailsCardLoadingState,
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): ConnectorDetailsCardLoadingState => {
  return {
    ...untranslatedVm,
    data: untranslatedVm.data && translateConnectorDetailsCardViewModel(untranslatedVm.data, translateService, kwPipe, siteId),
  };
};

export const translateConnectorDetailsCardViewModel = (
  data: UntranslatedConnectorDetailsCardViewModel,
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): ConnectorDetailsCardViewModel => {
  return {
    ...data,
    connectorEventTimelineVm: translateConnectorEventTimelineViewModel(
      data.connectorEventTimelineVm,
      [
        {
          id: data.connectorId,
          name: data.connectorName,
        },
      ],
      translateService,
      kwPipe,
      siteId
    ),
  };
};

export const translateConnectorEventTimelineViewModel = (
  data: UntranslatedConnectorEventTimelineViewModel,
  evses: {
    id: string;
    name: string;
  }[],
  translateService: TranslateService,
  kwPipe: KwPipe,
  siteId: string
): ConnectorEventTimelineViewModel => {
  return {
    ...data,
    events: data.events
      .filter((event) => isChargingTimelineEventVisible(event))
      .map((event) => mapChargingTimelineEventStoreModelToTimelineEvent(evses, event, translateService, kwPipe, siteId)),
  };
};
